/**
 * Prek visual stream helpers: OA routing + layout metadata for Crear-con-IA.
 * Family comes from question tipo (QuestionType.family_key), not from OA number.
 */

const PM_NT_PATTERNS = [
  /PM\s*NT\s*OA\s*0?(\d+)\b/i,
  /OA\s*PM\s*NT\d*\s*(\d+)\b/i,
  /OA\s*0?(\d+)\s*PM\s*NT\b/i,
  // Common DB labels without "PM NT" (Prek Math subject list)
  /^OA\s*0?(\d+)\b/i,
  /\bOA\s*0?(\d+)\b/i,
];

export const NT_PILOT_NUMBERS = new Set([1, 2, 3, 4, 5, 9, 10, 11, 12]);
export const MATH_PILOT_NUMBERS = new Set([6, 7, 8]);
export const PREK_VISUAL_OA_NUMBERS = new Set([
  ...NT_PILOT_NUMBERS,
  ...MATH_PILOT_NUMBERS,
]);

/** Stream preview heights from Acuarela v4 `--h` (not S/M/L shrink). */
export const FORMAT_STREAM_HEIGHT = {
  S: 210,
  M: 330,
  L: 340,
};

/** Per-OA Acuarela component heights (preferred over format bucket). */
export const OA_SCENE_HEIGHT = {
  1: 210,
  2: 330,
  3: 330,
  4: 220,
  5: 250,
  6: 260,
  7: 260,
  8: 210,
  9: 330,
  10: 340,
  11: 360,
  12: 430,
};

/** UX family metadata keyed by QuestionType.family_key. */
export const FAMILY_BY_KEY = {
  "patron-corporal": {
    key: "patron-corporal",
    label: "Patrón corporal",
    layout: "sequence5",
    slots: 5,
    format: "S",
    fit: "cover",
    ratio: 3.2258,
  },
  clasificacion: {
    key: "clasificacion",
    label: "Clasificación por relación",
    layout: "matching8",
    slots: 8,
    format: "M",
    fit: "contain",
    ratio: 1.7857,
  },
  ubicacion: {
    key: "ubicacion",
    label: "Ubicación espacial",
    layout: "scene5",
    slots: 5,
    format: "M",
    fit: "contain",
    ratio: 1.7857,
  },
  comparacion: {
    key: "comparacion",
    label: "Comparación de cantidades",
    layout: "choices3",
    slots: 3,
    format: "S",
    fit: "cover",
    ratio: 2.9762,
  },
  frecuencia: {
    key: "frecuencia",
    label: "Frecuencia cotidiana",
    layout: "choices3",
    slots: 3,
    format: "S",
    fit: "contain",
    ratio: 2.5,
  },
  conteo: {
    key: "conteo",
    label: "Conteo guiado",
    layout: "figure4",
    slots: 4,
    format: "S",
    fit: "contain",
    ratio: 2.6374,
  },
  "adicion-pelotas": {
    key: "adicion-pelotas",
    label: "Adición con objetos",
    layout: "equation3",
    slots: 3,
    format: "M",
    fit: "cover",
    ratio: 2.3,
  },
  perspectiva: {
    key: "perspectiva",
    label: "Perspectiva visual",
    layout: "scene5",
    slots: 5,
    format: "M",
    fit: "contain",
    ratio: 1.7857,
  },
  "figura-3d": {
    key: "figura-3d",
    label: "Figura tridimensional",
    layout: "figure4",
    slots: 4,
    format: "L",
    fit: "contain",
    ratio: 1.7857,
  },
  medicion: {
    key: "medicion",
    label: "Medición no convencional",
    layout: "choices3",
    slots: 3,
    format: "M",
    fit: "contain",
    ratio: 1.7857,
  },
  "historia-aditiva": {
    key: "historia-aditiva",
    label: "Historia matemática",
    layout: "story3",
    slots: 3,
    format: "M",
    fit: "contain",
    ratio: 1.6287,
  },
};

/** tipo slug → family_key (mirrors visual_prek_catalog.json QuestionType rows). */
export const FAMILY_KEY_BY_TIPO = {
  patron_corporal_guiado: "patron-corporal",
  clasificacion_relacion_lineas: "clasificacion",
  ubicacion_dentro_fuera: "ubicacion",
  ubicacion_linea: "ubicacion",
  comparacion_tres_casillas: "comparacion",
  temporal_seleccion_multiple: "frecuencia",
  vistas_objeto_elegir: "perspectiva",
  figuras_observa_responde: "figura-3d",
  figuras_marca_clasifica: "figura-3d",
  medicion_cuenta_opciones: "medicion",
  medicion_cuenta_escribe: "medicion",
  medicion_compara_unidades: "medicion",
  comunicar_proceso_problema: "historia-aditiva",
  numero: "conteo",
  escribir: "conteo",
  composicion_pictorica: "adicion-pelotas",
  operacion_simbolica: "adicion-pelotas",
};

/** First tipo typically used per OA for placeholder cards before GPT returns. */
const FIRST_TIPO_BY_OA_NUM = {
  1: "patron_corporal_guiado",
  2: "clasificacion_relacion_lineas",
  3: "ubicacion_dentro_fuera",
  4: "comparacion_tres_casillas",
  5: "temporal_seleccion_multiple",
  6: "numero",
  7: "numero",
  8: "composicion_pictorica",
  9: "vistas_objeto_elegir",
  10: "figuras_observa_responde",
  11: "medicion_cuenta_opciones",
  12: "comunicar_proceso_problema",
};

export const TEXT_FALLBACK_FAMILY = {
  key: "texto",
  label: "Enunciado y alternativas",
  layout: "choices3",
  slots: 4,
  format: "M",
  fit: "contain",
  ratio: 1.7857,
  height: FORMAT_STREAM_HEIGHT.M,
};

export function parsePmNtOaNumber(oaName = "") {
  const text = String(oaName || "").trim();
  if (!text) return null;
  for (const pattern of PM_NT_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      const num = Number(match[1]);
      if (num >= 1 && num <= 12) return num;
    }
  }
  return null;
}

/** Resolve PM NT OA number 1–12 from OA name (portable; no DB PK hardcoding). */
export function resolvePrekVisualOaNumber(oaId, oaName = "") {
  void oaId;
  return parsePmNtOaNumber(oaName);
}

export function isVisualNtPilot(oaId, oaName = "") {
  const num = resolvePrekVisualOaNumber(oaId, oaName);
  return num != null && NT_PILOT_NUMBERS.has(num);
}

export function isVisualMathPilot(oaId, oaName = "") {
  const num = resolvePrekVisualOaNumber(oaId, oaName);
  return num != null && MATH_PILOT_NUMBERS.has(num);
}

export function isVisualPilot(oaId, oaName = "") {
  const num = resolvePrekVisualOaNumber(oaId, oaName);
  return num != null && PREK_VISUAL_OA_NUMBERS.has(num);
}

/**
 * Visual stream for Prek/Kinder Matemáticas when OA is a Prek visual pilot (01–12).
 * Matching uses OA name (PM NT or plain "OA 01"); IDs are resolved server-side.
 */
export function shouldUseVisualStream(gradeCode, subjectName, oaId, oaName = "") {
  const grade = Number(gradeCode);
  if (grade !== 0 && grade !== 1) return false;
  const subject = String(subjectName || "").toLowerCase();
  const isMath =
    subject.includes("matem") ||
    subject.includes("pensamiento matem") ||
    subject === "matemáticas" ||
    subject === "matematicas";
  if (!isMath) return false;
  return isVisualPilot(oaId, oaName);
}

function withStreamMetrics(fam, oaNum = null) {
  return {
    ...fam,
    height:
      (oaNum != null && OA_SCENE_HEIGHT[oaNum]) ||
      FORMAT_STREAM_HEIGHT[fam.format] ||
      FORMAT_STREAM_HEIGHT.M,
    scale: 1,
    y: 0,
  };
}

/** Family metadata from a visual tipo slug (preferred). */
export function familyForTipo(tipo, oaNum = null) {
  const key = FAMILY_KEY_BY_TIPO[tipo];
  if (key && FAMILY_BY_KEY[key]) {
    return withStreamMetrics(FAMILY_BY_KEY[key], oaNum);
  }
  return { ...TEXT_FALLBACK_FAMILY };
}

/**
 * Placeholder family before the backend returns a tipo.
 * Uses the first tipo of the OA when the name parses; otherwise text fallback.
 */
export function familyForOa(oaName = "", oaId = null) {
  const num = resolvePrekVisualOaNumber(oaId, oaName);
  if (num != null && FIRST_TIPO_BY_OA_NUM[num]) {
    return familyForTipo(FIRST_TIPO_BY_OA_NUM[num], num);
  }
  return { ...TEXT_FALLBACK_FAMILY };
}

export function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
